import useAction from '@shared/hooks/useAction';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Reducer } from 'redux';

export const EVENT_TYPES = Object.freeze({
  ASSESSMENT_BATCH_COMPLETED: 'ASSESSMENT_BATCH_COMPLETED' as const,
  RECENTLY_ADDED_EXPERIENCE: 'RECENTLY_ADDED_EXPERIENCE' as const,
});

type EventType = keyof typeof EVENT_TYPES;

export type UserEvent = { type: EventType } & Record<string, unknown>;

const actionCreators = {
  eventTriggered: (...events: UserEvent[]) => ({
    type: 'USER_EVENTS_TRIGGERED' as const,
    data: { events },
  }),
  eventHandled: (...events: UserEvent[]) => ({
    type: 'USER_EVENTS_HANDLED' as const,
    data: { events },
  }),
};

const initialState = [] as UserEvent[];

type State = UserEvent[];
type Action = ReturnType<(typeof actionCreators)[keyof typeof actionCreators]>;

const userEvents: Reducer<State, Action> = (state = initialState, action) => {
  switch (action.type) {
    case 'USER_EVENTS_TRIGGERED':
      return [...state, ...action.data.events].slice(-50);
    case 'USER_EVENTS_HANDLED':
      return state.filter((event) => !action.data.events.includes(event));
    default:
      return state;
  }
};

export default userEvents;

export const useTriggerUserEvent = () => useAction(actionCreators.eventTriggered);

const rootSelector = (rootState: { userEvents: State }): State => rootState.userEvents;

export const useUserEvents = () => useSelector(rootSelector);

export const useAddRecentExperienceId = () => {
  const triggerEvent = useTriggerUserEvent();

  const addExperience = useCallback(
    (experienceId: number) => {
      triggerEvent({
        type: EVENT_TYPES.RECENTLY_ADDED_EXPERIENCE,
        experienceId,
      });
    },
    [triggerEvent],
  );

  return addExperience;
};

export const useConsumeRecentExperienceId = () => {
  const dispatch = useDispatch();
  const events = useUserEvents();
  const event = useMemo(
    () => events.find((e) => e.type === EVENT_TYPES.RECENTLY_ADDED_EXPERIENCE),
    [events],
  );

  useEffect(() => {
    if (event) {
      const cleanedUpEvents = events.filter(
        (e) => e.type === EVENT_TYPES.RECENTLY_ADDED_EXPERIENCE,
      );
      dispatch(actionCreators.eventHandled(...cleanedUpEvents));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return event?.experienceId as number | undefined;
};

export const usePopUserEvents = (
  matcher: (event: UserEvent) => boolean,
  handler?: (events: UserEvent[]) => void,
) => {
  const dispatch = useDispatch();
  const events = useUserEvents().filter(matcher);
  useEffect(() => {
    if (events.length) {
      handler?.(events);
      dispatch(actionCreators.eventHandled(...events));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const poppedEvents = useRef(events.length).current;
  return poppedEvents;
};
